import React from "react";
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useRouter } from "../../util/router";
import { useAuth } from "../../util/auth";
import SideNav from "./SideNav";
import StateFarmCheckModal from "./StateFarmCheckModal";
import ThreeCardRow from "./ThreeCardRow";
import ToggleButton from "./ToggleButton";
import LinkCard from "./LinkCard";
import AnalyticsCard from "./AnalyticsCard";
import PeopleCard from "./PeopleCard";
import TitleCard from "./TitleCard";
import DownloadButton from "./DownloadButton";
import ResourceCard from "./ResourceCard";
import { useCodesByOwner, useAllVideos, useTeamMembershipsByMember } from "../../util/db";
import { useState, useEffect } from "react";
import PieChartStatsCard from "./PieChartStatsCard";
import PeopleProgressCard from "./PeopleProgressCard";
import ResourceCompletionTable from "./ResourceCompletionTable";
import WebinarCard from "./WebinarCard";
import CodeDetails from "./CodeDetails";
import { trackPurchase } from "../../util/analytics";

function CustomDashboard(props) {
  const auth = useAuth();
  const router = useRouter()
  const [stats, setStats] = useState({})
  const [opened, setOpened] = useState(0)
  const [unOpened, setUnOpened] = useState(0)


  const [oldStats, setOldStats] = useState({})
  const [successfulPayment, setSuccessfulPayment] = useState(router.query.paid)


  const [isOpen, setIsOpen] = useState(false);
  const [option, setOption] = useState("Monthly")
  // Function to toggle the dropdown open and close
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Get team members directly from user document
  const teamMembers = auth.user?.teamMembers || [];

  // Check if user is a member of another team
  const { data: memberships } = useTeamMembershipsByMember(auth.user?.uid);
  // Filter out own document - only count as team member if in SOMEONE ELSE's teamMembers
  const isTeamMember = memberships?.some(m => m.id !== auth.user?.uid);

  const {
    data: videoCodes,
    status: videoCodesStatus,
    error: videoCodesError
  } = useCodesByOwner(auth.user.uid)

  const {
    data: allVideos,
    status: allVideosStatus,
    error: allVideosError
  } = useAllVideos()

  function calculatePercentage(old, current) {
    let difference = current - old
    let perc = old !== 0 ? (difference / old * 100) : 999
    if (old === 0 && current === 0) {
      perc = 0
    }
    return perc
  }

  // Get unique member IDs including self
  function getMemberIds() {
    const members = [...teamMembers];
    if (!members.includes(auth.user.uid)) {
      members.push(auth.user.uid);
    }
    return members;
  }
  function calculateStats(rawdata) {
    let yeardata = []
    let monthdata = []
    let weekdata = []
    let openedTemp = 0
    let unOpenedTemp = 0

    // Track actual watch seconds
    let totalWatchSeconds = 0;
    let yearWatchSeconds = 0;
    let monthWatchSeconds = 0;
    let weekWatchSeconds = 0;

    const currentTime = new Date().getTime();
    rawdata?.forEach(object => {
      if (object.views > 0) {
        openedTemp += 1
      } else {
        unOpenedTemp += 1
      }

      // Get watch seconds (fallback to 5 min estimate for old data without watchSeconds)
      const seconds = object.watchSeconds ?? (object.views || 0) * 300;
      totalWatchSeconds += seconds;

      if (currentTime - (object.createdAt.seconds * 1000) < 365 * 60 * 60 * 24 * 1000) {
        yeardata.push(object)
        yearWatchSeconds += seconds;

        if (currentTime - (object.createdAt.seconds * 1000) < 30 * 60 * 60 * 24 * 1000) {
          monthdata.push(object)
          monthWatchSeconds += seconds;

          if (currentTime - (object.createdAt.seconds * 1000) < 7 * 60 * 60 * 24 * 1000) {
            weekdata.push(object)
            weekWatchSeconds += seconds;
          }
        }
      }
    })
    let totalviews = calculateTotalViews(rawdata)
    let yearviews = calculateTotalViews(yeardata)
    let monthviews = calculateTotalViews(monthdata)
    let weekviews = calculateTotalViews(weekdata)
    setOpened(openedTemp)
    setUnOpened(unOpenedTemp)
    return {
      shares: { week: weekdata?.length, month: monthdata?.length, year: yeardata?.length, all: rawdata?.length },
      views: { week: weekviews, month: monthviews, year: yearviews, all: totalviews },
      hours: {
        week: (weekWatchSeconds / 3600).toFixed(2),
        month: (monthWatchSeconds / 3600).toFixed(2),
        year: (yearWatchSeconds / 3600).toFixed(2),
        all: (totalWatchSeconds / 3600).toFixed(2)
      },
      sales: { week: 0, month: 0, year: 0, all: 0 }
    }
  }
  function calculateOldStats(rawdata) {
    let yeardata = []
    let monthdata = []
    let weekdata = []

    // Track watch seconds for previous periods
    let yearWatchSeconds = 0;
    let monthWatchSeconds = 0;
    let weekWatchSeconds = 0;

    const currentTime = new Date().getTime();
    rawdata?.forEach(object => {
      const ageMs = currentTime - (object.createdAt.seconds * 1000);
      // Get watch seconds (fallback to 5 min estimate for old data)
      const seconds = object.watchSeconds ?? (object.views || 0) * 300;

      // Previous week (7-14 days ago)
      if (ageMs > 7 * 60 * 60 * 24 * 1000 && ageMs < 14 * 60 * 60 * 24 * 1000) {
        weekdata.push(object)
        weekWatchSeconds += seconds;
      }
      // Previous month (30-60 days ago)
      if (ageMs > 30 * 60 * 60 * 24 * 1000 && ageMs < 60 * 60 * 60 * 24 * 1000) {
        monthdata.push(object)
        monthWatchSeconds += seconds;
      }
      // Previous year (365-730 days ago)
      if (ageMs > 365 * 60 * 60 * 24 * 1000 && ageMs < 730 * 60 * 60 * 24 * 1000) {
        yeardata.push(object)
        yearWatchSeconds += seconds;
      }
    })
    let yearviews = calculateTotalViews(yeardata)
    let monthviews = calculateTotalViews(monthdata)
    let weekviews = calculateTotalViews(weekdata)
    return {
      shares: { week: weekdata?.length, month: monthdata?.length, year: yeardata?.length },
      views: { week: weekviews, month: monthviews, year: yearviews },
      hours: {
        week: (weekWatchSeconds / 3600).toFixed(2),
        month: (monthWatchSeconds / 3600).toFixed(2),
        year: (yearWatchSeconds / 3600).toFixed(2)
      },
      sales: { week: 0, month: 0, year: 0 }
    }
  }
  function calculateTotalViews(objects) {
    let sum = 0
    objects?.forEach(object => {
      if (object.views) {
        sum += object.views
      }
    })
    return sum
  }

  useEffect(() => {
    setStats(calculateStats(videoCodes))
    setOldStats(calculateOldStats(videoCodes))

  }, [videoCodes])

  useEffect(() => {
    if (successfulPayment && router.query.product) {
      trackPurchase(router.query.product);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <>

      <>
        {successfulPayment && (
          <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
                <div>
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                    <svg
                      className="h-6 w-6 text-green-700"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Payment successful</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Thank you for your purchase. We look forward to working with you!
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm"
                    onClick={() => setSuccessfulPayment(false)}
                  >
                    Continue to Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>

      <div className="container">
        {(router.query.section === "dashboard") && (
          <>
            <div className="flex flex-row justify-between items-center pb-4">
              <ToggleButton sectionsArray={[
                { section: "dashboard", label: "Dashboard" },
                { section: "analytics", label: "Analytics" },
              ]} urlPrefix={"/dashboard"} section={router.query.section} />
            </div>



            <div className="flex w-full flex-col lg:flex-row">
              <div className="w-full lg:w-2/3 px-4">
                <TitleCard title="All-Time Progress">
                  {(videoCodesStatus === "success" && videoCodes) && (
                    <AnalyticsCard showChange={false}
                      hours={stats.hours?.all}
                      sales={stats.sales?.all}
                      shares={stats.shares?.all}
                      views={stats.views?.all} />
                  )}
                  <ThreeCardRow></ThreeCardRow>
                </TitleCard>

              </div>
              <div className="w-full lg:w-1/3 px-4">
                <TitleCard title="Live Training Schedule">
                  <WebinarCard />
                </TitleCard>
                {!isTeamMember && (
                  <LinkCard title="Team Members" href="/teams/all">
                    <PeopleCard uid={auth.user.uid} />
                  </LinkCard>
                )}
              </div>
            </div>
            {(allVideosStatus === "success" && allVideos && allVideos?.length > 2) && (
              <LinkCard title="Training Resources" subtitle={`${allVideos.length} Available Resources`} href="/tools/sp">
                <ThreeCardRow>
                  {allVideos.filter((each) => !each.itemId?.includes("shuff")).map((each, i) => <ResourceCard href={(i === 0) ? '/tools/ip' : `/tools/${each.itemId}`} title={each.title} description={each.description} photo={each.coverPhoto} key={i} />
                  )}
                </ThreeCardRow>
              </LinkCard>
            )}

          </>
        )}
        {(router.query.section === "analytics") && (
          <div className="container">
            <div className="flex flex-row justify-between items-center">
              <ToggleButton sectionsArray={[
                { section: "dashboard", label: "Dashboard" },
                { section: "analytics", label: "Analytics" },
              ]} urlPrefix={"/dashboard"} section={router.query.section} />
              <div className="flex space-x-2">
                <div className="relative inline-block text-left">
                  {/* Button to toggle the dropdown */}
                  <button onClick={toggleDropdown} className="flex flex-row bg-white text-green-500 py-2 px-4 rounded-md border-2 border-green-200 focus:outline-none focus:border-green-700">
                    {option}
                    <ChevronDownIcon className='h-6 ml-0.5' />
                  </button>

                  {/* Dropdown Menu */}
                  {isOpen && (
                    <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                      <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
                        <button className="text-green-500 block px-4 py-2 text-sm w-full text-left hover:bg-green-100" role="menuitem" onClick={() => {
                          toggleDropdown()
                          setOption("Weekly")
                        }}>Weekly</button>
                        <button className="text-green-500 block px-4 py-2 text-sm w-full text-left hover:bg-green-100" role="menuitem" onClick={() => {
                          toggleDropdown()
                          setOption("Monthly")
                        }}>Monthly</button>
                        <button className="text-green-500 block px-4 py-2 text-sm w-full text-left hover:bg-green-100" role="menuitem" onClick={() => {
                          toggleDropdown()
                          setOption("Yearly")
                        }}>Yearly</button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="lg:block hidden">
                  <DownloadButton />
                </div>
              </div>
            </div>
            <div className="flex w-full lg:flex-row lg:space-x-4 flex-col">
              <div className="flex-grow-1 lg:w-3/4 w-full">
                <TitleCard title="Individual Access Codes Sent">
                  <PieChartStatsCard opened={opened} unopened={unOpened} />
                </TitleCard>
                {(videoCodesStatus === "success" && videoCodes) && (<>
                  <AnalyticsCard
                    showChange={true}
                    sales={(option === "Weekly") ? stats.sales.week : (option === "Monthly") ? stats.sales.month : stats.sales.year}
                    salePerc={(option === "Weekly") ? calculatePercentage(oldStats.sales.week, stats.sales.week) : (option === "Monthly") ? calculatePercentage(oldStats.sales.month, stats.sales.month) : calculatePercentage(oldStats.sales.year, stats.sales.year)}
                    hours={(option === "Weekly") ? stats.hours.week : (option === "Monthly") ? stats.hours.month : stats.hours.year}
                    hourPerc={(option === "Weekly") ? calculatePercentage(oldStats.hours.week, stats.hours.week) : (option === "Monthly") ? calculatePercentage(oldStats.hours.month, stats.hours.month) : calculatePercentage(oldStats.hours.year, stats.hours.year)}
                    shares={(option === "Weekly") ? stats.shares.week : (option === "Monthly") ? stats.shares.month : stats.shares.year}
                    sharePerc={(option === "Weekly") ? calculatePercentage(oldStats.shares.week, stats.shares.week) : (option === "Monthly") ? calculatePercentage(oldStats.shares.month, stats.shares.month) : calculatePercentage(oldStats.shares.year, stats.shares.year)}
                    viewPerc={(option === "Weekly") ? calculatePercentage(oldStats.views.week, stats.views.week) : (option === "Monthly") ? calculatePercentage(oldStats.views.month, stats.views.month) : calculatePercentage(oldStats.views.year, stats.views.year)}
                    views={(option === "Weekly") ? stats.views.week : (option === "Monthly") ? stats.views.month : stats.views.year} />
                  <TitleCard title="This Month's Codes">
                    <CodeDetails codes={videoCodes} />
                  </TitleCard>
                </>
                )}
              </div>
              <div className="flex-grow">
                <TitleCard title="Team Webinar Completion" subtitle={`Team Member Progress`} >
                  <div className="border border-green-700 rounded-lg">
                    {allVideos && allVideosStatus === "success" && (
                      <ResourceCompletionTable ids={getMemberIds()} videos={allVideos} />
                    )}

                  </div>
                </TitleCard>
              </div>
            </div>
            <TitleCard title="Team Utilization Analytics" subtitle="Team Member Statistics">
              <PeopleProgressCard teamMembers={teamMembers} />
            </TitleCard>
          </div>
        )}

      </div>
      <SideNav current="dashboard"></SideNav>
      <StateFarmCheckModal onDone={() => null} />

    </>

  );
}

export default CustomDashboard;
